<template>
  <div class='w-full h-full bg-white flex'>
    <div class='w-7/12 px-8'>
      <settings-item-header :title="'Integrations'"
                            :description="'Manage your integrations with external services.'"
                            :is-adding-new-item="isAddingNewIntegration"
                            class="border-none"
                            @click="isAddingNewIntegration ? cancelAddNewIntegration() : addNewIntegration()"
      />
      <with-loader id="settings-integrations-table-loader" :loading="loaders.integrations" class="h-full pb-10" use-as-overlay>
        <div v-if="isEmpty(integrations)"
             class="h-3/5 flex flex-col justify-center items-center">
          <span class="text-center text-gray-dark mt-8">
            No integrations yet. Create your first integration.
          </span>
        </div>
        <settings-integrations-list v-else-if="!loaders.integrations"
                            :integrations="sortedIntegrations"
                            :bot-name-map="botNameMap"
                            :selected-integration-index="selectedIntegrationIndex"
                            :selected-channel="selectedIntegrationChannel"
                            @select-integration-index="selectIntegrationIndex"
                            @select-channel-index="selectChannelIndex"
                            @request-delete="handleRequestDelete"
                            class="h-5/6"
        />
      </with-loader>
    </div>

    <transition name="slide-fade" mode="out-in">
      <settings-integration-editor v-if="selectedIntegration"
                           :integration="selectedIntegration"
                           :key="selectedIntegration.id"
                           class="w-5/12"
                           @update-integration="updateIntegration"
                           @request-delete="handleRequestDelete"
      />
      <settings-integration-create v-else-if="isAddingNewIntegration"
                           @process-new-integration="processNewIntegration"
                           class="w-5/12"
      />
    </transition>

    <delete-dialog v-if="isDeleteDialogShown"
                   :is-shown="isDeleteDialogShown"
                   :item="integrationToDelete"
                   :dialog-type="'confirm'"
                   :delete-title="'Delete Integration?'"
                   :delete-description="deleteDescription"
                   :delete-confirm-text="'Delete Integration'"
                   @cancel="cancelDelete"
                   @delete="deleteIntegration"
    >
      <template #delete-item-icon>
        <delete-bot-icon class="h-8 w-8 text-gray-dark"/>
      </template>
    </delete-dialog>
  </div>
</template>

<script>
import DeleteBotIcon from '@/assets/images/icons/DeleteBotIcon'
import DeleteDialog from '@/components/common/DeleteDialog.vue'
import SettingsItemHeader from '@/components/settings/SettingsItemHeader.vue'
import SettingsIntegrationsList from '@/components/settings/integrations/SettingsIntegrationsList.vue'
import SettingsIntegrationEditor from '@/components/settings/integrations/SettingsIntegrationEditor.vue'
import SettingsIntegrationCreate from '@/components/settings/integrations/SettingsIntegrationCreate.vue'
import WithLoader from '@/components/common/WithLoader.vue'
import integrationService from '@/services/amio/integration.service'
import botService from '@/services/amio/bot.service'
import isEmpty from 'ramda/src/isEmpty'
import prepend from 'ramda/src/prepend'
import sortBy from 'ramda/src/sortBy'
import toaster from '@/utils/toaster'
import { ROUTE_NAMES } from '@/router'
import { ITEM_IDS } from '@/utils/editor.constants'
import { addIconsToIntegrationChannels } from '@/utils/integration.utils'
import busyButtonsModule from '@/store/modules/busy-buttons.module'
import { BUSY_BUTTONS } from '@/utils/busy-buttons.constants'

export default {
  name: 'SettingsIntegrations',
  components: {
    DeleteBotIcon,
    DeleteDialog,
    SettingsItemHeader,
    SettingsIntegrationsList,
    SettingsIntegrationEditor,
    SettingsIntegrationCreate,
    WithLoader
  },

  watch: {
    '$route.params': {
      immediate: true,
      handler(params) {
        if (params.integrationId === ITEM_IDS.NEW) {
          this.selectedIntegrationIndex = -1
        } else {
          this.selectIntegrationIndexByRoute()
        }
      }
    }
  },

  data() {
    return {
      integrations: [],
      bots: [],
      selectedIntegrationIndex: -1,
      selectedChannelIndex: -1,
      loaders: {
        integrations: false
      },
      integrationToDelete: null,
      isDeleteDialogShown: false,
      isEmpty
    }
  },

  computed: {
    sortedIntegrations() {
      return sortBy(integration => integration.type?.toLowerCase() || '')(this.integrations)
    },

    botNameMap() {
      const map = {}
      this.bots.forEach(bot => {
        map[bot.id] = bot.name || 'Unnamed'
      })
      return map
    },

    deleteDescription() {
      return this.integrationToDelete ? `This will permanently delete the integration "${this.integrationToDelete.type}". This action cannot be undone.` : ''
    },

    selectedIntegration() {
      return isEmpty(this.integrations) && this.selectedIntegrationIndex >= 0 ? null : this.sortedIntegrations[this.selectedIntegrationIndex]
    },

    selectedIntegrationChannel() {
      if (!this.selectedIntegration || this.selectedChannelIndex === -1) return null

      return this.selectedIntegration?.channels?.[this.selectedChannelIndex]
    },

    isAddingNewIntegration() {
      return this.$route.path.includes(ITEM_IDS.NEW)
    }
  },

  methods: {
    addNewIntegration() {
      this.selectedIntegrationIndex = -1
      this.$router.push({ name: ROUTE_NAMES.integrations, params: { integrationId: ITEM_IDS.NEW } })
    },

    cancelAddNewIntegration() {
      if (!isEmpty(this.integrations)) {
        this.$router.push({
          name: ROUTE_NAMES.integrations,
          params: {
            integrationId: this.sortedIntegrations[0].id
          }
        })
      } else {
        this.$router.push({
          name: ROUTE_NAMES.integrations
        })
      }
    },

    selectIntegrationIndex(integrationIndex) {
      if (this.selectedIntegrationIndex === integrationIndex) return
      this.selectedIntegrationIndex = integrationIndex
      this.selectedChannelIndex = -1
    },

    selectChannelIndex(channelIndex) {
      this.selectedChannelIndex = channelIndex
    },

    selectIntegrationIndexByRoute() {
      if (isEmpty(this.integrations)) return

      const routeIntegrationId = this.$route.params.integrationId
      const routeChannelId = this.$route.params.channelId

      if (routeIntegrationId) {
        this.selectedIntegrationIndex = this.sortedIntegrations.findIndex(integration => integration.id === routeIntegrationId)

        if (this.selectedIntegrationIndex >= 0) {
          const integration = this.sortedIntegrations[this.selectedIntegrationIndex]

          if (routeChannelId && integration.channels) {
            this.selectedChannelIndex = integration.channels.findIndex(channel => channel.id === routeChannelId)
          } else {
            this.selectedChannelIndex = -1
          }
        }
        return
      }

      this.$router.replace({
        name: ROUTE_NAMES.integrations,
        params: {
          integrationId: this.sortedIntegrations[0].id
        }
      })
      this.selectedIntegrationIndex = 0
      this.selectedChannelIndex = -1
    },

    handleRequestDelete(integration) {
      this.integrationToDelete = integration
      this.isDeleteDialogShown = true
    },

    cancelDelete() {
      this.integrationToDelete = null
      this.isDeleteDialogShown = false
    },

    async deleteIntegration() {
      busyButtonsModule.setButtonBusy(BUSY_BUTTONS.ITEM_DELETE, true)
      try {
        const botId = this.integrationToDelete.bot_id
        if (!botId) {
          throw new Error('Bot ID is missing from integration')
        }
        await integrationService.deleteIntegration(botId, this.integrationToDelete.id)
        const updatedIntegrationList = this.integrations.filter(integration => integration.id !== this.integrationToDelete.id)
        this.integrations = updatedIntegrationList
        
        if (isEmpty(updatedIntegrationList)) {
          this.addNewIntegration()
        } else {
          const selectedIntegration = this.sortedIntegrations[0]
          await this.$router.replace({
            name: ROUTE_NAMES.integrations,
            params: {
              integrationId: selectedIntegration.id
            }
          })
          this.selectedIntegrationIndex = 0
        }
        toaster.success('Integration deleted successfully')
      } catch (e) {
        toaster.error(e)
      } finally {
        busyButtonsModule.setButtonBusy(BUSY_BUTTONS.ITEM_DELETE, false)
        this.integrationToDelete = null
        this.isDeleteDialogShown = false
      }
    },

    async processNewIntegration(newIntegration) {
      this.integrations = prepend(addIconsToIntegrationChannels(newIntegration), this.integrations)
      await this.$router.replace({
        name: ROUTE_NAMES.integrations,
        params: {
          integrationId: newIntegration.id
        }
      })
    },

    async updateIntegration(updatedIntegration) {
      const index = this.integrations.findIndex(integration => integration.id === updatedIntegration.id)
      if (index >= 0) {
        this.integrations = [...this.integrations.slice(0, index), addIconsToIntegrationChannels(updatedIntegration), ...this.integrations.slice(index + 1)]
      }
    },

    async loadBots() {
      try {
        this.bots = await botService.listBots()
      } catch (err) {
        // Silently fail - bot names will just not be displayed
        console.error('Failed to load bots for integration list:', err)
      }
    }
  },

  async mounted() {
    this.loaders.integrations = true
    
    // Load bots in parallel (non-blocking)
    this.loadBots()
    
    try {
      const integrations = await integrationService.listIntegrations()
      this.integrations = integrations.map(addIconsToIntegrationChannels)
      
      if (isEmpty(this.integrations)) {
        this.loaders.integrations = false
        this.addNewIntegration()
        return
      }

      if (!this.$route.path.includes(ITEM_IDS.NEW)) this.selectIntegrationIndexByRoute()
    } catch (err) {
      toaster.error(err)
    } finally {
      this.loaders.integrations = false
    }
  }
}
</script>

<style scoped>
.slide-fade-enter-active {
  transition: all .3s ease;
}

.slide-fade-leave-active {
  transition: all .3s cubic-bezier(1.0, 0.5, 0.8, 1.0);
}

.slide-fade-enter, .slide-fade-leave-to {
  transform: translateX(10px);
  opacity: 0;
}
</style>

